import React from 'react';
import { useLocation, useNavigation } from 'react-router-dom';
import * as Headless from '@headlessui/react';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import {
  CheckCircleIcon,
  CloudArrowDownIcon,
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';

export type PendingChangesSaveMessages = {
  successTitle: string;
  successDescription: string;
  errorTitle: string;
  errorDescriptionPrefix: string;
};

export const pendingChangesSaveMessages = {
  agency: {
    successTitle: 'Agency information saved',
    successDescription: 'Your changes have been saved.',
    errorTitle: 'Error saving agency information',
    errorDescriptionPrefix: 'There was an error saving:'
  },
  voiceAgent: {
    successTitle: 'Settings saved',
    successDescription: 'Your settings have been saved and your voice agent has been updated.',
    errorTitle: 'Error saving settings',
    errorDescriptionPrefix: 'There was an error saving your settings:'
  }
} satisfies Record<string, PendingChangesSaveMessages>;

export function usePendingChangesSave<TSaved, TMutation>(params: {
  initialSavedState: TSaved;
  action: ActionResult<TMutation> | undefined;
  saveSuccessTrigger?: unknown;
  isSaveSuccessful: (action: ActionResult<TMutation> | undefined) => boolean;
  getSavedStateAfterCommit?: () => TSaved;
  onAfterSave?: (savedState: TSaved) => void;
  clearPendingOn?: 'commit' | 'idle';
}) {
  const navigation = useNavigation();
  const location = useLocation();
  const clearPendingOn = params.clearPendingOn ?? 'commit';

  const [changesPending, setChangesPending] = React.useState(false);
  const [savedState, setSavedState] = React.useState(params.initialSavedState);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (params.action?.applicationError || params.action?.serverError) {
      setError(
        params.action.applicationError?.message || params.action.serverError?.message || null
      );
    } else {
      setError(null);
    }
  }, [params.action?.applicationError, params.action?.serverError]);

  React.useEffect(() => {
    if (
      !params.action?.validationErrors?.length &&
      navigation.state === 'loading' &&
      navigation.location.pathname === location.pathname
    ) {
      setSaved(true);
    }
  }, [
    params.action?.validationErrors,
    navigation.state,
    navigation.location?.pathname,
    location.pathname
  ]);

  // The toast dismisses on its own clock rather than the navigation's. Arming
  // the timer in the effect above tied it to navigation.state, so finishing the
  // navigation ran the cleanup and cancelled the timer a fraction of a second
  // in, leaving the toast up until the next render that happened to clear it.
  React.useEffect(() => {
    if (!saved) {
      return;
    }
    const timer = window.setTimeout(() => setSaved(false), 5000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  React.useEffect(() => {
    if (clearPendingOn !== 'idle') {
      return;
    }
    if (navigation.state === 'idle' && params.isSaveSuccessful(params.action)) {
      setChangesPending(false);
    }
  }, [clearPendingOn, navigation.state, params.action, params.saveSuccessTrigger]);

  React.useEffect(() => {
    if (!params.isSaveSuccessful(params.action)) {
      return;
    }
    const nextSavedState = params.getSavedStateAfterCommit?.() ?? savedState;
    setSavedState(nextSavedState);
    if (clearPendingOn === 'commit') {
      setChangesPending(false);
    }
    params.onAfterSave?.(nextSavedState);
  }, [params.saveSuccessTrigger]);

  const markPendingChanges = React.useCallback(() => {
    setChangesPending(true);
  }, []);

  const discardChanges = React.useCallback(
    (setForm: React.Dispatch<React.SetStateAction<TSaved>>) => {
      setForm(savedState);
      setChangesPending(false);
    },
    [savedState]
  );

  return {
    changesPending,
    saved,
    error,
    markPendingChanges,
    discardChanges,
    savedState
  };
}

export function withPendingChanges<T>(
  markPendingChanges: () => void,
  updateForm: (path: string[], updater: (prev: T) => T) => void
) {
  return (path: string[], updater: (prev: T) => T) => {
    updateForm(path, updater);
    markPendingChanges();
  };
}

/** A toast for a save; a success-only toast leaves out error and the error messages. */
export function PendingChangesSaveToast(props: {
  saved: boolean;
  error?: string | null;
  messages: Pick<PendingChangesSaveMessages, 'successTitle' | 'successDescription'> &
    Partial<PendingChangesSaveMessages>;
}) {
  return (
    <AnimatePresence>
      {props.saved && (
        <motion.div
          role="status"
          initial={{ opacity: 0, translateY: 18 }}
          animate={{ opacity: 1, translateY: 0 }}
          exit={{ opacity: 0, translateY: 18 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="z-50 fixed top-8 right-8 pointer-events-auto w-full max-w-sm rounded-lg bg-white shadow-lg outline-1 outline-black/5 dark:bg-gray-800 dark:-outline-offset-1 dark:outline-white/10"
        >
          <div className="p-4">
            <div className="flex items-start">
              <div className="shrink-0">
                {!props.error && (
                  <CheckCircleIcon aria-hidden="true" className="size-6 text-green-400" />
                )}
                {props.error && <XCircleIcon aria-hidden="true" className="size-6 text-red-400" />}
              </div>
              <div className="ml-3 w-0 flex-1 pt-0.5">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {props.error ? props.messages.errorTitle : props.messages.successTitle}
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {props.error
                    ? `${props.messages.errorDescriptionPrefix} ${props.error}`
                    : props.messages.successDescription}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function PendingChangesSaveBar(props: {
  changesPending: boolean;
  onDiscard: () => void;
  deployLabel?: string;
}) {
  const navigation = useNavigation();

  return (
    <AnimatePresence>
      {props.changesPending && (
        <motion.div
          className="fixed lg:left-1/2 left-[calc(50%-100px)] bottom-12 shadow-xl border border-neutral-100 dark:border-neutral-700 rounded-full bg-white dark:bg-neutral-800 p-2 z-10 flex gap-2"
          initial={{ opacity: 0, translateY: 100, scale: 0.9 }}
          animate={{ opacity: 1, translateY: 0, scale: 1 }}
          exit={{ opacity: 0, translateY: 100, scale: 0.9 }}
          transition={{ duration: 0.15, ease: 'easeInOut' }}
        >
          <Headless.Button
            className={clsx(
              'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
              'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
              'text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 border-dashed hover:bg-neutral-100/50 dark:hover:bg-white/10'
            )}
            type="button"
            onClick={props.onDiscard}
          >
            <div>Discard pending changes</div>
            <div>
              <XMarkIcon className="size-3.5" />
            </div>
          </Headless.Button>
          <Headless.Button
            className={clsx(
              'flex items-center gap-x-1 rounded-full px-3 py-1.5 text-xs font-medium flex-shrink-0',
              'focus:outline-none after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:ring-inset after:ring-transparent after:focus-within:ring-0 after:focus-within:ring-neutral-300 dark:after:focus-within:ring-white/20',
              navigation.state !== 'submitting' &&
                'text-white bg-blue-600/80 border border-transparent hover:bg-blue-600/90',
              navigation.state === 'submitting' &&
                'text-white bg-blue-400/80 border border-transparent hover:bg-blue-400/90'
            )}
            type="submit"
          >
            <CloudArrowDownIcon className="size-3.5" />
            {props.deployLabel ?? 'Save and Deploy Changes'}
            {navigation.state === 'submitting' && <Spinner size="size-3.5" color="white" />}
          </Headless.Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function PendingChangesFormHeader(props: {
  title: string;
  description: string;
  backHref?: string;
  backLabel?: string;
  changesPending: boolean;
  saveLabel?: string;
}) {
  const navigation = useNavigation();

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium m-0 p-0 text-neutral-900 dark:text-white">
            {props.title}
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">{props.description}</p>
        </div>
        <div className="flex gap-2">
          <Button
            type="submit"
            color="sky"
            loading={navigation.state === 'submitting'}
            disabled={!props.changesPending}
          >
            {props.saveLabel ?? 'Save Changes'}
          </Button>
          {props.backHref && (
            <Button color="white" href={props.backHref}>
              {props.backLabel ?? 'Back'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
